// Angular
import { Injectable } from '@angular/core';
// Services
import {ApiService, SystemApiService} from '@cv-invision/core';
// Models
import { OrderInboxModel } from '../model/order-inbox.model';
// Others
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {HttpClient, HttpParams} from '@angular/common/http';
import { InboxFilterForm } from '../model/inbox-form-filter.model';
import { ResubmitCreatedOrder } from '../resubmit-order/resubmit-order.component';

@Injectable({ providedIn: 'root' })
export class OrderInboxService {
	private headerInboxSubject = new BehaviorSubject({});
	currentHeaderInbox = this.headerInboxSubject.asObservable();
	constructor(
		private httpClient: HttpClient,
		private systemApiService: SystemApiService,
		private api: ApiService) {}

	/**
	 * Get current inbox Orders
	 */
	public getReviewerInbox(params: InboxFilterForm): Observable<OrderInboxModel[]> {

		let httpParams = new HttpParams();

		if (params.inboxGlobalFilter && params.inboxGlobalFilter.filters) {
			const { searchType, formTypes, sortBy } = params.inboxGlobalFilter.filters;
			httpParams = httpParams.append('searchType', searchType);

			sortBy.forEach((property: any) => {
				httpParams = httpParams.append('sortBy', property);
			});
		}
		if (params.orderStates.length) {
			params.orderStates.forEach((state: any) => {
				httpParams = httpParams.append('OrderState', state.name);
			});
		}
		if (params.startDate && params.startDate !== null) {
			httpParams = httpParams.append(
				'StartDate',
				params.startDate ? new Date(params.startDate).toISOString() : ''
			);
		}
		if (params.endDate && params.endDate !== null) {
			httpParams = httpParams.append(
				'EndDate',
				params.endDate ? new Date(params.endDate).toISOString() : '');
		}
		if (params.address) {
			httpParams = httpParams.append('Description', params.address);
		}

		if (params.clientName) {
			httpParams = httpParams.append('Client', params.clientName);
		}

		if (params.referenceID) {
			httpParams = httpParams.append('ExternalId', params.referenceID);
		}
		if (params.ssrFilterOrders && params.ssrFilterOrders.id != null) {
			const hasSsrRules = !!params.ssrFilterOrders.id;
			httpParams = httpParams.append('hasSsrRules', hasSsrRules.toString());
		}
		if (params.formType.length) {
			params.formType.forEach((form: any) => {
				httpParams = httpParams.append('FormType', form.name);
			});
		}

		httpParams = httpParams.append('limit', params.pagination.limit.toString());
		httpParams = httpParams.append('offset', params.pagination.offset.toString());
		if (params.profileSelected  && params.profileSelected.id) {
			httpParams = httpParams.append('OrderedBy', params.profileSelected.id.toString());
		}

		return this.httpClient.get(
			this.systemApiService.api.intelligenceService + 'order-details/search', {params: httpParams})
			.pipe(
				map((payload: any) => {
					return payload;
				})
			);
	}

	/**
	 * Get the main picture to show in the Inbox Detail
	 */
	public getInboxMainPicture(orderId: number): Observable<OrderInboxModel> {
		return this.httpClient.get(this.systemApiService.api.intelligenceService +
			`orders/${orderId}/documents/current/attachments?category=PhotoRequest`)
			.pipe(
				map((payload: any) => {
					let mainPicture;
					if (payload.data && payload.data.length) {
						mainPicture = payload.data.find((attachment: any) => {
							return (
								attachment.metadata &&
								(attachment.metadata.photoType === 'VIEW_FRONT' ||
									attachment.metadata.photoType === 'STRUCTURE_FRONT')
							);
						});

						// If not mainPicture let's get the first image from the attachments
						if (!mainPicture) {
							mainPicture = payload.data.find((attachment: any) => {
								return attachment.contentType && (attachment.contentType === 'image/jpeg' || attachment.contentType === 'image/jpg') &&
									attachment.groupName === 'Subject';
							});
						}
					}
					return mainPicture;
				})
			);
	}

	/**
	 * Get all profiles
	 */
	public getProfiles(): Observable<any[]> {
		return this.httpClient.get(this.systemApiService.api.intelligenceService + `profiles`)
			.pipe(
				map((payload: any) => {
					const profiles = [];
					if (payload.data && payload.data.length) {
						payload.data.forEach((profile: any) => {
							profiles.push({
								id: profile.id,
								name: profile.firstName + ' ' + profile.lastName
							});
						});
					}
					return profiles;
				})
			);
	}

	/**
	 * re-open an order
	 * @param orderId
	 */
	public reOpenOrder(orderId: number): Observable<any> {
		return this.httpClient.put(
			this.systemApiService.api.intelligenceService + `orders/${orderId}/reopen`, {}
		).pipe(map(payload => payload));
	}

	/**
	 * re-submit an order that was imported
	 */
	public reSubmitOrderImported(orderId: any): Observable<any> {
		return this.httpClient.put(
			this.systemApiService.api.intelligenceService + `orders/${orderId}/resubmit/import`, {}
		).pipe(
			map(payload => payload)
		);
	}

	/**
	 * re-submit an order that was creaded via xml
	 * @param payloadForm
	 */
	public reSubmitOrderCreated(payloadForm: ResubmitCreatedOrder): Observable<any> {
		const formFile = new FormData();
		formFile.append('file', payloadForm.file);

		return this.httpClient.put(
			this.systemApiService.api.intelligenceService + `upload/orders/${payloadForm.orderId}/resubmit`, formFile)
			.pipe(
				map(payload => payload)
			);
	}

	/**
	 * Sets new value for the Header <-> Inbox component data communication.
	 * @param data
	 */
	setHeaderInboxData(data: any): void {
		this.headerInboxSubject.next(data);
	}

	/**
	 *
	 * @param orderId string
	 */
	public getOrderAutoQcStatus(orderId: string): Observable<any> {
		return this.httpClient.get(`${this.systemApiService.api.intelligenceService}smartQc/process/${orderId}`);
	}
}
